const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Popover } = require('@mui/material/Popover');
const { default: Tooltip } = require('@mui/material/Tooltip');
const { default: AddReactionIcon } = require('@mui/icons-material/AddReaction');
const { default: Dialog } = require('@mui/material/Dialog');
const { default: DialogTitle } = require('@mui/material/DialogTitle');
const { default: DialogContent } = require('@mui/material/DialogContent');
const { default: DialogActions } = require('@mui/material/DialogActions');
const { default: Button } = require('@mui/material/Button');

const { REACTIONS } = require('utils/constants');

const internals = {};
const REACTION_BUTTON_SIZE = 22;
const REACTION_BADGE_HEIGHT = 22;
const REACTION_HINT_HEIGHT = 21;
const REACTION_HINT_OFFSET = 42;

const ReactionButton = ({ onAddReaction, onRemoveReaction, existingReactions, messageId, channelSid, className, userId, usersNameMap, forceShow, canAddReaction = true }) => {

    const containerRef = React.useRef(null);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedReaction, setSelectedReaction] = React.useState(null);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [layout, setLayout] = React.useState({
        hasHint: false,
        isRight: false
    });
    const canInteractWithReactions = canAddReaction;
    const canRemoveOwnReaction = canInteractWithReactions;
    const open = canInteractWithReactions && Boolean(anchorEl);
    const normalizedCurrentUserId = React.useMemo(() => internals.normalizeUserId(userId), [userId]);
    const hasReactions = React.useMemo(() => {

        return existingReactions && Object.values(existingReactions).some((users) => users.length > 0);
    }, [existingReactions]);
    const containerStyle = React.useMemo(() => internals.getContainerStyle({
        canAddReaction: canInteractWithReactions,
        forceShow,
        hasReactions
    }), [canInteractWithReactions, forceShow, hasReactions]);

    React.useLayoutEffect(() => {

        const nextLayout = internals.resolveLayout(containerRef.current);

        setLayout((previousLayout) => {

            if (previousLayout.hasHint === nextLayout.hasHint && previousLayout.isRight === nextLayout.isRight) {
                return previousLayout;
            }

            return nextLayout;
        });
    });

    React.useLayoutEffect(() => {

        internals.positionReactionHintText(containerRef.current, layout);
    }, [layout]);

    const handleClick = (event) => {

        if (!canInteractWithReactions) {
            return;
        }

        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {

        setAnchorEl(null);
    };

    const handleReactionClick = (reaction) => {

        const hasReacted = internals.hasReactionFromUser(existingReactions?.[reaction], normalizedCurrentUserId);

        if (!canInteractWithReactions && !hasReacted) {
            return;
        }

        if (hasReacted) {
            onRemoveReaction({
                messageSid: messageId,
                channelSid,
                reaction,
                userId
            });
        }
        else {
            onAddReaction({
                messageSid: messageId,
                channelSid,
                reaction,
                userId
            });
        }

        handleClose();
    };

    const handleShowReactors = (reaction) => {

        setSelectedReaction(reaction);
        setModalOpen(true);
    };

    const handleCloseModal = () => {

        setModalOpen(false);
        setSelectedReaction(null);
    };

    const getUserNames = (userIds) => {

        if (!userIds || !usersNameMap) {
            return [];
        }

        return userIds.map((id) => internals.getUserName(id, usersNameMap)).filter(Boolean);
    };

    return (
        <ReactionContainer
            ref={containerRef}
            className={className}
            style={containerStyle}
        >
            {existingReactions && Object.entries(existingReactions).map(([reaction, users]) => {

                if (users.length > 0) {
                    const tooltipTitle = getUserNames(users);

                    return (
                        <Tooltip
                            key={reaction}
                            title={
                                <div style={{ whiteSpace: 'pre-line' }}>
                                    {tooltipTitle.join('\n')}
                                </div>
                            }
                            placement="top"
                            arrow
                        >
                            <ReactionBadge
                                onClick={() => handleShowReactors(reaction)}
                                isActive={canRemoveOwnReaction && internals.hasReactionFromUser(users, normalizedCurrentUserId)}
                            >
                                {REACTIONS[reaction]} {users.length}
                            </ReactionBadge>
                        </Tooltip>
                    );
                }

                return null;
            })}
            {canInteractWithReactions && (
                <ReactionIconButton
                    onClick={handleClick}
                    size='small'
                    aria-label='Add Reaction'
                >
                    <AddReactionIcon fontSize='small' />
                </ReactionIconButton>
            )}
            <StyledPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                <ReactionList>
                    {Object.entries(REACTIONS).map(([key, emoji]) => (

                        <ReactionItem
                            key={key}
                            onClick={() => handleReactionClick(key)}
                            isActive={internals.hasReactionFromUser(existingReactions?.[key], normalizedCurrentUserId)}
                        >
                            {emoji}
                        </ReactionItem>
                    ))}
                </ReactionList>
            </StyledPopover>
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="reaction-users-dialog-title"
            >
                <DialogTitle id="reaction-users-dialog-title">
                    {selectedReaction && `Users who reacted with ${REACTIONS[selectedReaction]}`}
                </DialogTitle>
                <DialogContent>
                    {selectedReaction && existingReactions?.[selectedReaction]?.map((reactorId) => {

                        const isOwnReaction = internals.normalizeUserId(reactorId) === normalizedCurrentUserId;

                        return (
                            <ReactionUserItem
                                key={reactorId}
                                isOwn={canRemoveOwnReaction && isOwnReaction}
                                onClick={() => {

                                    if (canRemoveOwnReaction && isOwnReaction) {
                                        handleReactionClick(selectedReaction);
                                        handleCloseModal();
                                    }
                                }}
                            >
                                {internals.getUserName(reactorId, usersNameMap)}
                                {canRemoveOwnReaction && isOwnReaction && (
                                    <RemoveText>(tap to remove)</RemoveText>
                                )}
                            </ReactionUserItem>
                        );
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Close</Button>
                </DialogActions>
            </Dialog>
        </ReactionContainer>
    );
};

module.exports = ReactionButton;
module.exports.internals = internals;

internals.normalizeUserId = (value) => {

    if (value === null || typeof value === 'undefined') {
        return null;
    }

    if (typeof value === 'number') {
        return value;
    }

    const stringValue = String(value).trim();

    if (/^\d+$/.test(stringValue)) {
        return Number(stringValue);
    }

    const trailingNumericSegment = stringValue.match(/(\d+)(?!.*\d)/);

    if (trailingNumericSegment) {
        return Number(trailingNumericSegment[1]);
    }

    return stringValue;
};

internals.getUserName = (reactorId, usersNameMap = {}) => {

    const normalizedUserId = internals.normalizeUserId(reactorId);
    const candidateKeys = [
        reactorId,
        typeof reactorId === 'string' ? reactorId.trim() : reactorId,
        normalizedUserId,
        normalizedUserId !== null && typeof normalizedUserId !== 'undefined' ? String(normalizedUserId) : null
    ].filter((key) => key !== null && typeof key !== 'undefined' && key !== '');

    const matchingKey = candidateKeys.find((key) => usersNameMap[key]);

    return matchingKey ? usersNameMap[matchingKey] : 'Unknown User';
};

internals.hasReactionFromUser = (reactorIds = [], targetUserId) => {

    if (targetUserId === null || typeof targetUserId === 'undefined') {
        return false;
    }

    return reactorIds.some((reactorId) => internals.normalizeUserId(reactorId) === targetUserId);
};

internals.classNameIncludes = (element, needle) => {

    const className = typeof element?.className === 'string' ? element.className : '';

    return className.includes(needle);
};

internals.resolveLayout = (element) => {

    const wrapper = element?.closest?.('[class*="chatItemWrapper"]');
    const bubble = element?.closest?.('[class*="talkBubble"]');

    return {
        hasHint: internals.classNameIncludes(bubble, 'hasReactionHint'),
        isRight: internals.classNameIncludes(wrapper, 'right')
    };
};

internals.positionReactionHintText = (element, layout) => {

    const bubble = element?.closest?.('[class*="talkBubble"]');
    const hintText = bubble?.querySelector?.('[class*="addReactionText"]');

    if (!hintText) {
        return;
    }

    if (!layout.hasHint) {
        hintText.style.removeProperty('left');
        hintText.style.removeProperty('right');
        hintText.style.removeProperty('bottom');
        hintText.style.removeProperty('height');
        hintText.style.removeProperty('min-height');
        hintText.style.removeProperty('padding');
        hintText.style.removeProperty('font-size');
        hintText.style.removeProperty('line-height');
        hintText.style.removeProperty('box-sizing');
        return;
    }

    hintText.style.bottom = '-12px';
    hintText.style.height = `${REACTION_HINT_HEIGHT}px`;
    hintText.style.minHeight = `${REACTION_HINT_HEIGHT}px`;
    hintText.style.padding = '2px 7px';
    hintText.style.fontSize = '11px';
    hintText.style.lineHeight = '14px';
    hintText.style.boxSizing = 'border-box';
    hintText.style.left = 'auto';
    hintText.style.right = `${REACTION_HINT_OFFSET}px`;
};

internals.getContainerStyle = ({ canAddReaction, forceShow, hasReactions }) => {

    const isVisible = hasReactions || (canAddReaction && forceShow);

    return {
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.2s',
        bottom: '-13px',
        left: 'auto',
        right: '14px',
        maxWidth: 'calc(100% - 16px)',
        height: `${REACTION_BADGE_HEIGHT}px`,
        minHeight: `${REACTION_BADGE_HEIGHT}px`,
        padding: 0,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center'
    };
};

ReactionButton.propTypes = {
    onAddReaction: T.func.isRequired,
    onRemoveReaction: T.func.isRequired,
    existingReactions: T.object,
    messageId: T.string.isRequired,
    channelSid: T.string,
    className: T.string,
    userId: T.number.isRequired,
    usersNameMap: T.object,
    forceShow: T.bool,
    canAddReaction: T.bool
};

const ReactionContainer = Styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-height: ${REACTION_BADGE_HEIGHT}px;
    box-sizing: border-box;
`;

const ReactionIconButton = Styled(IconButton)`
    width: ${REACTION_BUTTON_SIZE + 4}px !important;
    height: ${REACTION_BUTTON_SIZE}px !important;
    padding: 2px !important;
    border-radius: 999px !important;
    background: #eef2f6 !important;
    border: 1px solid #d8dee7 !important;
    color: #667085 !important;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08) !important;

    svg {
        font-size: 16px !important;
    }

    &:hover,
    &.Mui-focusVisible {
        background: #e4e9f0 !important;
        box-shadow: 0 2px 5px rgba(16, 24, 40, 0.10) !important;
    }
`;

const StyledPopover = Styled(Popover)`
    .MuiPopover-paper {
        border-radius: 20px;
        padding: 4px;
        background-color: #ffffff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
`;

const ReactionList = Styled.div`
    display: flex;
    gap: 2px;
    padding: 2px;
`;

const ReactionItem = Styled.button`
    border: none;
    background: none;
    font-size: 18px;
    padding: 0px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
        background-color: #f0f0f0;
    }

    ${({ isActive }) => isActive && `
        background-color: #e3f2fd;
        &:hover {
            background-color: #e3f2fd;
        }
    `}
`;

const ReactionBadge = Styled.button`
    border: 1px solid ${({ isActive }) => isActive ? '#b9d8f7' : '#d8dee7'};
    background-color: ${({ isActive }) => isActive ? '#dbeafe' : '#f8fafc'};
    border-radius: 11px;
    padding: 2px 7px;
    font-size: 12px;
    line-height: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
    height: ${REACTION_BADGE_HEIGHT}px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    &:hover {
        background-color: ${({ isActive }) => isActive ? '#dbeafe' : '#eef2f6'};
    }
`;

const ReactionUserItem = Styled.div`
    padding: 8px;
    margin: 4px 0;
    border-radius: 4px;
    cursor: ${({ isOwn }) => isOwn ? 'pointer' : 'default'};
    background-color: ${({ isOwn }) => isOwn ? '#f5f5f5' : 'transparent'};

    &:hover {
        background-color: ${({ isOwn }) => isOwn ? '#eeeeee' : 'transparent'};
    }
`;

const RemoveText = Styled.div`
    color: #666;
    font-size: 11px;
    margin-top: 2px;
`;
